import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiService } from 'api';
import { ERROR_404, ERROR_504 } from 'common/config/api';
import {
  PDF_EXPORT_QUERY_PARAM_EXPORT_MODE,
  PDF_EXPORT_QUERY_PARAM_EXPORT_TAB,
} from 'common/constants/pdfExport';
import { useFirmScopedPermissions
} from 'common/hooks';
import { useStore } from 'common/store';
import type { SelectedMeasurementDate } from 'common/types/measurementDate';
import { FundByMeasurementDate, SetPageFilters, UseStoreValues } from 'common/types/store';
import ExcelExportContext from 'context/ExcelExportContext';
import LayoutContext, { type FirmBaseLayoutLegacyContextValue } from 'context/LayoutContext';
import { useZoom, ZOOM_CONFIG } from 'context/ZoomContext';
import { useCheckReportFile, useDocuments } from 'services/hooks';

const KEEP_POLLING_STATUS_CODES = [Number(ERROR_404), Number(ERROR_504)];

interface FirmBaseLayoutLegacyBridgeProps {
  children: ReactNode;
}

// Temporary bridge for firm route children that still read header/sidebar-era LayoutContext fields.
// New Prism navigation state belongs in FirmBaseLayoutContext instead.
const FirmBaseLayoutLegacyBridge = ({ children }: FirmBaseLayoutLegacyBridgeProps) => {
  const [{ firmId, user }] = useStore() as UseStoreValues;
  const { currentFirmRole, isViewOnlyUser: viewOnlyUser } = useFirmScopedPermissions({ firmId, user });
  const { downloadDocument } = useDocuments();
  const [searchParams] = useSearchParams();
  const { setZoomLevel } = useZoom();

  // PDF export drives capture through hidden iframes that load the page with
  // ?exportMode=true[&exportTab=...]. Every exportable section reads these flags
  // off LayoutContext (useExportSignal/useExportLoadingBridge, EXPORT_MODE_STYLES),
  // so they must come from the URL — matching the legacy Main layout.
  const isExportMode = useMemo(
    () => searchParams.get(PDF_EXPORT_QUERY_PARAM_EXPORT_MODE) === 'true',
    [searchParams]
  );
  const exportTab = useMemo(() => searchParams.get(PDF_EXPORT_QUERY_PARAM_EXPORT_TAB) ?? '', [searchParams]);

  // Capture spreadsheets at 100% zoom so the PDF output matches the legacy layout.
  useEffect(() => {
    if (isExportMode) {
      setZoomLevel(ZOOM_CONFIG.EXPORT_MODE);
    }
  }, [isExportMode, setZoomLevel]);

  const [pageFilters, setPageFilters] = useState<SetPageFilters[] | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [openBulkActionsDialog, setOpenBulkActionsDialog] = useState(false);
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [currentMeasurementDate, setCurrentMeasurementDateState] = useState<SelectedMeasurementDate>(null);
  const [pageType, setPageTypeState] = useState<string | null>(null);
  const [fundsByMeasurementDate, setFundsByMeasurementDate] = useState<FundByMeasurementDate[]>([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSuccessForIndividualReport = useCallback(
    (onSuccessData: any, requestData: any) =>
      downloadDocument({ file: onSuccessData, firmId: requestData.firm }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // intentional stable ref — matches original Main.tsx pattern
  );

  const {
    doReportPollCheck,
    reportRequestData,
    reportUUID,
    setDoReportPollCheck,
    setReportRequestData,
    setReportUUID,
    currentSelectedValues,
    setCurrentSelectedValues,
    isLoading: isExcelExportLoading,
  } = useCheckReportFile({
    onSuccessFn: onSuccessForIndividualReport,
    keepPollingStatusCodes: KEEP_POLLING_STATUS_CODES,
    successMessage: 'Excel is downloading',
    defaultErrorMessage: 'An error occurred while checking the Excel Report',
    maxAttemptsMessage:
      'Looks like the report is taking longer than expected. Please check the document\'s page later under the \'Reports\' folder.',
    infoMessage:
      'The report is taking longer than expected due to server timeouts. Please check the document\'s page later under the \'Reports\' folder.',
    pollingFn: ApiService.apiInvestorAppCheckExcelReportCreate,
  });

  const excelExportContextValue = useMemo(
    () => ({
      doReportPollCheck,
      reportRequestData,
      reportUUID,
      setDoReportPollCheck,
      setReportRequestData,
      setReportUUID,
      currentSelectedValues,
      setCurrentSelectedValues,
      isLoading: isExcelExportLoading,
      fundsByMeasurementDate,
      setFundsByMeasurementDate,
    }),
    [
      doReportPollCheck,
      reportRequestData,
      reportUUID,
      setDoReportPollCheck,
      setReportRequestData,
      setReportUUID,
      currentSelectedValues,
      setCurrentSelectedValues,
      isExcelExportLoading,
      fundsByMeasurementDate,
    ]
  );

  // Old header actions no longer have a visual host in the Prism shell. Keep no-op
  // callbacks so legacy route children can unmount safely until their actions move to FirmBaseLayoutContext.
  const updatePageActions = useCallback<FirmBaseLayoutLegacyContextValue['updatePageActions']>(
    () => undefined,
    []
  );
  const updateExtraPageActions = useCallback<FirmBaseLayoutLegacyContextValue['updateExtraPageActions']>(
    () => undefined,
    []
  );

  const toggleBulkActionsDialog = useCallback(() => setOpenBulkActionsDialog(v => !v), []);
  const togglePdfDialog = useCallback(() => setOpenPdfDialog(v => !v), []);
  const setCurrentMeasurementDate = useCallback(
    (md: SelectedMeasurementDate) => setCurrentMeasurementDateState(md),
    []
  );
  const setPageType = useCallback((pt?: string | null) => setPageTypeState(pt ?? null), []);

  const layoutContextValue = useMemo<FirmBaseLayoutLegacyContextValue>(
    () => ({
      updatePageActions,
      pageFilters,
      setPageFilters,
      updateExtraPageActions,
      showAlert,
      setShowAlert,
      viewOnlyUser,
      currentFirmRole,
      openBulkActionsDialog,
      toggleBulkActionsDialog,
      openPdfDialog,
      togglePdfDialog,
      isExportMode,
      exportTab,
      currentMeasurementDate: currentMeasurementDate ?? undefined,
      setCurrentMeasurementDate,
      pageType,
      setPageType,
    }),
    [
      pageFilters,
      updatePageActions,
      updateExtraPageActions,
      showAlert,
      viewOnlyUser,
      currentFirmRole,
      openBulkActionsDialog,
      openPdfDialog,
      toggleBulkActionsDialog,
      togglePdfDialog,
      isExportMode,
      exportTab,
      currentMeasurementDate,
      setCurrentMeasurementDate,
      pageType,
      setPageType,
    ]
  );

  return (
    <ExcelExportContext.Provider value={excelExportContextValue as any}>
      <LayoutContext.Provider value={layoutContextValue}>{children}</LayoutContext.Provider>
    </ExcelExportContext.Provider>
  );
};

export default FirmBaseLayoutLegacyBridge;
